import DOMPurify from 'dompurify';
import { CapturedImage, ImageCaptureInput } from '@ecosystem/newron-design-system/build/export-utils/captureImage';

const ALLOWED_DATA_URL = /^data:image\/(?:svg\+xml|png);base64,[a-z0-9+/=]*$/i;

const safeSetImgSrc = (img: HTMLImageElement, url: string) => {
  const sanitized = DOMPurify.sanitize(url, { ALLOWED_URI_REGEXP: ALLOWED_DATA_URL }) as string;
  if (!ALLOWED_DATA_URL.test(sanitized)) throw new Error('Blocked image src');
  img.src = sanitized;
};

export const svgToPng = (svg: CapturedImage | CapturedImage[]): Promise<string> => {
  return new Promise((resolve, reject) => {
    const imageData = Array.isArray(svg) ? svg[0] : svg;
    if (!imageData?.dataUrl) {
      return reject(new Error('Invalid SVG data provided'));
    }
    const { dataUrl, width, height } = imageData;
    let canvas: HTMLCanvasElement;
    let ctx: CanvasRenderingContext2D | null;
    let img = new Image();

    safeSetImgSrc(img, dataUrl);
    img.onload = () => {
      canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      ctx = canvas.getContext('2d');
      ctx!.drawImage(img, 0, 0, img.width, img.height, 0, 0, width as number, height as number);

      img = new Image();
      safeSetImgSrc(img, canvas.toDataURL('image/png'));
      img.onload = () => {
        canvas = document.createElement('canvas');
        canvas.width = width as number;
        canvas.height = height as number;
        ctx = canvas.getContext('2d');
        ctx!.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
    };
    img.onerror = reject;
  });
};

export const toBinaryStr = (str: string) => {
  const encoder = new TextEncoder();
  return encoder.encode(str).reduce((acc, code) => acc + String.fromCharCode(code), '');
};

export const convertToSvgImage = async (input: ImageCaptureInput): Promise<CapturedImage> => {
  const { ref, title, width, height } = input;
  const tempDiv = document.createElement('div');
  Object.assign(tempDiv.style, {
    position: 'absolute',
    opacity: '0',
    pointerEvents: 'none',
  });

  let svgNode: Element;
  const content = ref.current;

  if (content instanceof Node) {
    svgNode = content.cloneNode(true) as Element;
  } else {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content as string, 'image/svg+xml');
    ['script', 'iframe', 'object', 'embed'].forEach((tag) => doc.querySelectorAll(tag).forEach((el) => el.remove()));
    svgNode = doc.documentElement;
  }

  tempDiv.appendChild(svgNode);
  document.body.appendChild(tempDiv);

  const svgElement = tempDiv.querySelector('svg');
  if (!svgElement) {
    document.body.removeChild(tempDiv);
    throw new Error('No SVG element found');
  }

  const measuredWidth =
    (width as number | undefined) ??
    parseFloat(svgElement.getAttribute('width') ?? '') ??
    svgElement.getBoundingClientRect().width ??
    0;

  const measuredHeight =
    (height as number | undefined) ??
    parseFloat(svgElement.getAttribute('height') ?? '') ??
    svgElement.getBoundingClientRect().height ??
    0;

  const svgString = new XMLSerializer().serializeToString(svgElement);
  const dataUrl = `data:image/svg+xml;base64,${window.btoa(toBinaryStr(svgString))}`;

  document.body.removeChild(tempDiv);

  return {
    title,
    dataUrl,
    width: measuredWidth,
    height: measuredHeight,
  };
};
